export const categories = ['reason', 'math', 'reading', 'foundations'] as const;
export type Category = (typeof categories)[number];
export const labels: Record<Category, string> = {
  reason: 'Reason',
  math: 'Math',
  reading: 'Read & Recall',
  foundations: 'Refresh Foundations',
};

export interface Source {
  title: string;
  url: string;
  note?: string;
}
export type ReadingBlock =
  | { kind: 'paragraph'; text: string }
  | {
      kind: 'equation';
      label: string;
      lines: {
        left: string;
        right: string | { numerator: string; denominator: string };
        reason?: string;
      }[];
      note?: string;
    }
  | { kind: 'example'; title: string; blocks: ReadingBlock[] }
  | { kind: 'table'; caption: string; headers: string[]; rows: string[][] }
  | {
      kind: 'diagram';
      caption: string;
      description: string;
      drawing:
        | 'conditional-grid'
        | 'arrival-area'
        | 'coin-states'
        | 'net-force'
        | 'energy-flow'
        | 'cone-similarity'
        | 'coordinate-distance'
        | 'wave-frequency'
        | 'circuit-paths'
        | 'domino-cases'
        | 'ribbon-cut'
        | 'plant-transport'
        | 'market-demand'
        | 'market-balance'
        | 'market-shift';
    };
export interface ReadingSection {
  id: string;
  title: string;
  blocks: ReadingBlock[];
  role?: 'Concept' | 'Method' | 'Worked example' | 'Daily application' | 'Check';
}
export interface Reading {
  title: string;
  sections: ReadingSection[];
  sources: Source[];
  authorship: string;
}
export interface Option {
  id: string;
  text: string;
}
export interface Question {
  id: string;
  prompt: string;
  options?: Option[];
  answer:
    | { kind: 'choice'; value: string }
    | { kind: 'number'; value: number; tolerance: number; unit?: string };
  explanation: string;
  sectionIds: string[];
  /** Editorial traceability, never displayed before submission. */
  evidence: string;
  /** Exact article section supporting a Read & Recall question. */
  sourceSection?: Source;
}
export interface Exercise {
  id: string;
  category: Category;
  title: string;
  topic: string;
  difficulty: 'Moderate' | 'Challenging';
  /** Read & Recall uses an external source, not a locally generated chapter. */
  article?: Source & {
    publisher?: string;
    reviewedOn: string;
    preview: string;
    scope: { wordCount: number; sections: (Source & { wordCount: number; anchors: string[] })[] };
  };
  reading: Reading;
  questions: Question[];
  /** Kept only so existing browser-history entries survive editorial replacements. */
  retiredQuestions?: Question[];
}
export interface Attempt {
  tries: number;
  correct: boolean;
  lastAnswer: string;
}
export interface HistoryEntry {
  questionId: string;
  exerciseId: string;
  addedAt: string;
}
export type Subject = Category | 'maps';
export const subjects: Subject[] = [...categories, 'maps'];
export const subjectLabels: Record<Subject, string> = { ...labels, maps: 'Learning Maps' };

export interface Progress {
  version: 1;
  /** Per-date choices for the home checklist; omitted in older saves. */
  dailySubjects?: Record<string, Subject[]>;
  dailyConfirmed?: Record<string, boolean>;
  dailyStamps?: Record<string, boolean | 'sun' | 'moon'>;
  attempts: Record<string, Attempt>;
  drafts: Record<string, string>;
  history: HistoryEntry[];
  completions: Record<string, Category[]>;
  /** Legacy field retained for saved-data compatibility; never gates questions. */
  readingStarted: Record<string, boolean>;
}
